// SPDX-License-Identifier: AGPL-3.0-or-later

import type { Questionnaire, QuestionnaireResponse } from '@intakeform/fhir';
import type { Finding } from '@intakeform/ui';

export interface PublicUser {
  id: string;
  organizationId: string;
  email: string;
  displayName: string;
  role: string;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  settings: Record<string, unknown>;
  /** Set when this clinic is linked to an Astrum organization for SSO. */
  externalOrgId?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface MeResponse {
  user: PublicUser;
  organization: Organization;
}

export interface QuestionnaireSummary {
  id: string;
  title: string;
  status: string;
  version: number;
  updatedAt: string;
}

export interface QuestionnaireListResponse {
  items: QuestionnaireSummary[];
}

export interface QuestionnaireResource {
  id: string;
  organizationId: string;
  title: string;
  status: string;
  version: number;
  formType?: string | null;
  resource: Questionnaire;
}

export interface TemplateListResponse {
  items: QuestionnaireResource[];
}

export interface ResponseSummary {
  id: string;
  questionnaireId: string;
  patientId: string;
  status: string;
  flagged: boolean;
  archived: boolean;
  submittedAt: string | null;
  updatedAt: string;
  patient?: {
    email: string | null;
    phone: string | null;
    displayName: string | null;
  };
}

export interface ResponseListResponse {
  items: ResponseSummary[];
}

/**
 * Terminal status values — once a response reaches one of these, a
 * clinician has signed off and the response no longer "needs
 * attention" from the operational perspective the dashboards use.
 * The `flagged` boolean stays set in the DB as an audit fact (this
 * submission did trip a finding); UI counters use
 * `isActivelyFlagged` so the badge / "Flagged" tab clear once the
 * response is signed off.
 */
const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'verified',
  'completed',
  'entered-in-error',
]);

/**
 * True when the response is flagged AND still in a status that
 * needs clinician attention. Used by the sidebar Responses badge,
 * the Nurse-view "Flagged" tab + count, and the dashboard's
 * "Clinical Flags" stat. Keep these three call sites consistent
 * by going through this helper.
 */
export function isActivelyFlagged(r: ResponseSummary): boolean {
  return r.flagged && !TERMINAL_STATUSES.has(r.status);
}

export interface ResponseDetail {
  resource: QuestionnaireResponse;
  findings: Finding[];
  /** linkId → human-readable question text, joined from the parent
   * Questionnaire. Falls back to {} when the parent has been deleted. */
  itemTexts: Record<string, string>;
  questionnaireId: string;
  status: string;
  flagged: boolean;
  /**
   * Finding codes that tripped the clinically-significant partition
   * at submit time (intakeform-preview-6hy). Empty when flagged=false.
   * The detail page renders these as chips beside the Flagged badge.
   */
  flaggedCodes: string[];
  submittedAt: string | null;
  questionnaire?: Questionnaire;
  patient?: {
    email: string | null;
    phone: string | null;
    displayName: string | null;
  };
  /**
   * Latest insurance eligibility check for this response (bead 27w).
   * `null` when no check has run — either the form had no insurance
   * fields, or the fire-and-forget background check hasn't completed
   * yet. The UI surfaces a per-status card on the response detail.
   */
  eligibility?: ResponseEligibility | null;
}

export interface ResponseEligibility {
  status: 'active' | 'inactive' | 'unknown' | 'error';
  planName: string | null;
  providerKind: string;
  checkedAt: string;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface MagicLinkChannel {
  channel: 'sms' | 'email' | 'console';
}

/**
 * The channels this deployment can deliver on (intakeform-preview-2a0). The
 * sender is fixed at boot, so this is a deployment property the browser has to
 * be told rather than assume.
 */
export interface MagicLinkChannelsResponse {
  channels: MagicLinkChannel['channel'][];
}

export interface CreateMagicLinkBody {
  questionnaireId: string;
  patient: {
    email?: string;
    phone?: string;
    displayName?: string;
    /** The patient's EHR id — NOT the MRN; the two are different keys. */
    externalRef?: string;
    /** Practice-issued medical record number (eif). */
    mrn?: string;
  };
  filler?: {
    relationshipCode: string;
    email?: string;
    phone?: string;
    displayName?: string;
    externalRef?: string;
  };
  /**
   * How the link is SENT. Omit to send nothing and hand the link over on
   * screen instead, in which case `display` must be true
   * (intakeform-preview-lmxg). Only a channel this deployment reports on
   * GET /api/magic-links/channels will be accepted.
   */
  channel?: MagicLinkChannel['channel'];
  /**
   * Return the link in the response so staff can show it at the desk
   * (intakeform-preview-lmxg). Orthogonal to `channel`: with one it is
   * "show and send", without one it is "show only".
   */
  display?: boolean;
  /**
   * "Yes, this really is the same patient" (intakeform-preview-fjhe). The API
   * refuses a suspected duplicate with 409 until this is set, so a clinician
   * answers BEFORE a credential exists rather than being told after.
   */
  confirmReusePatient?: boolean;
  /**
   * Per-link TTL override in seconds (intakeform-preview-xv2). Wins
   * over the form's link-ttl-seconds extension + the org's
   * magic_link_ttl_seconds setting + the env default. Omit to use
   * the precedence chain.
   */
  ttlSeconds?: number;
}

export interface CreateMagicLinkResponse {
  id: string;
  expiresAt: string;
  /**
   * The magic link itself, returned only when `display` was requested
   * (intakeform-preview-lmxg). Distinct from `devLink` on purpose: this one
   * is returned in production, which is the whole point, while devLink is a
   * developer convenience production suppresses.
   */
  link?: string;
  /**
   * The outcome of the send half, present only when a channel was given.
   * `ok: false` means the link stands — the clinician has it on screen — but
   * no copy reached the patient, so they need to follow up.
   */
  delivery?: { channel: MagicLinkChannel['channel']; ok: boolean };
  devLink?: string;
  devJwt?: string;
  /**
   * Non-blocking warnings (in-3uo). Today the only code is
   * `duplicate-patient-suspect`, fired when the issuance reused a
   * patient row whose `display_name` or `external_ref` disagreed with
   * the values supplied on this request. The modal surfaces a confirm
   * dialog so the clinician can reuse or cancel-and-fix.
   */
  warnings?: MagicLinkWarning[];
}

export interface MagicLinkWarning {
  code: 'duplicate-patient-suspect' | string;
  existing?: {
    id: string;
    displayName?: string;
    externalRef?: string;
    mrn?: string;
  };
}

export interface PrefillItem {
  value: unknown;
  answeredAt: string | null;
  stale: boolean;
}

export interface IntakeSubject {
  displayName: string;
}

export interface IntakeFiller {
  displayName: string;
  relationshipCode: string;
  relationshipDisplay: string;
}

/**
 * Whether this link can offer EMR-sourced demographics, and whether the
 * patient has to confirm who they are first (intakeform-preview-200.7.9).
 *
 * Carries no EMR data itself — only whether a question will be asked. Chart
 * data reaches the browser after the answer, never before.
 */
export type EmrPrefillState =
  | { state: 'unavailable' }
  | { state: 'needs_identity'; field: 'birthDate' }
  | { state: 'locked' }
  | { state: 'verified' };

export interface IntakeBundle {
  questionnaire: Questionnaire;
  prefill: Record<string, PrefillItem>;
  /** Present when the deployment has EMR prefill wired up. */
  emrPrefill?: EmrPrefillState;
  /**
   * In-progress draft for this magic link (intakeform-preview-kva).
   * Populated when the patient auto-saved earlier in this link's
   * lifetime and the tab closed before submit. The renderer
   * hydrates from the draft AFTER prefill so the patient's own
   * answers win over prefill defaults. `null` when no draft
   * exists, or when the draft was authored against a different
   * questionnaire id than the link's current form (the server
   * discards stale drafts on form forks).
   */
  draft?: QuestionnaireResponse | null;
  /** Who the form is *about*. Always present. */
  subject?: IntakeSubject;
  /** Set only when a caregiver / parent is filling on the patient's behalf. */
  filler?: IntakeFiller;
  organizationSettings?: {
    brand_name?: string;
    brand_color?: string;
    brand_radius?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  } | null;
}

export interface DraftBody {
  resource: QuestionnaireResponse;
}

export interface DraftResult {
  savedAt: string;
}

export interface SubmitResult {
  id: string;
  findings: Finding[];
}

export interface AttachmentPresignBody {
  linkId: string;
  kind: 'signature' | 'file';
  contentType: string;
  sizeBytes: number;
}

export interface AttachmentPresignResponse {
  attachmentId: string;
  uploadUrl: string;
  expiresAt: string;
}

export interface AttachmentReadyBody {
  sha256?: string;
}
